<div [class]="customClasses().formGroup">
    @if (!inputGroupAppendEnabled() && !inputGroupPrependEnabled()) {
        <ng-container>
            @if (secondaryLabel()) {
                <div class="row">
                    <div class="col">
                        <label [class]="customClasses().label">{{ label() }}</label>
                    </div>
                    <div class="col-auto">
                        <small [class]="customClasses().secondaryLabel">{{ secondaryLabel() }}</small>
                    </div>
                </div>
            } @else {
                <label [class]="customClasses().label" [class.disabled]="ngControl.disabled">
                    {{ label() }}
                    @if (inputRequiredIndicatorEnabled()) {
                        <span class="text-danger"> *</span>
                    }
                </label>
            }

            @if (inputIcon()) {
                <div class="position-relative has-input-icon">
                    <span [class]="inputIcon() + ' form-control-feedback'"></span>
                    <ng-template [ngTemplateOutlet]="plainInput"></ng-template>
                </div>
            } @else {
                <ng-template [ngTemplateOutlet]="plainInput"></ng-template>
            }
        </ng-container>
    } @else {
        <label [class]="customClasses().label" [class.disabled]="ngControl.disabled">
            {{ label() }}
            @if (inputRequiredIndicatorEnabled()) {
                <span class="text-danger"> *</span>
            }
        </label>
        <div
            class="input-group"
            [class.is-invalid]="ngControl.touched && ngControl.invalid"
            [class.disabled]="ngControl.disabled"
            [class]="customClasses()?.inputGroup"
        >
            @if (inputGroupPrependEnabled()) {
                @if (inputGroupPrependText()) {
                    <div class="input-group-text" [class]="customClasses()?.inputGroupLabel">
                        {{ inputGroupPrependText() }}
                    </div>
                }
                @if (inputGroupPrependButtonText()) {
                    <button
                        [class]="inputGroupPrependButtonCustomClasses()?.button"
                        type="button"
                        (click)="onPrependClick()"
                        [disabled]="inputGroupPrependButtonProcessing() || inputGroupPrependButtonDisabled()"
                    >
                        @if (inputGroupPrependButtonProcessing()) {
                            <span
                                [class]="inputGroupPrependButtonCustomClasses()?.spinner"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        }
                        {{ inputGroupPrependButtonText() }}
                    </button>
                }
            }

            @if (!inputDecimalCheckEnabled()) {
                <input
                    [class.is-invalid]="ngControl.touched && ngControl.invalid"
                    [type]="type()"
                    [class]="customClasses().input"
                    [formControl]="ngControl.control | formControl"
                    [placeholder]="placeholder() ?? ''"
                    (keyup.enter)="onKeyUpEnter()"
                    [attr.data-qa]="testAutomationSelectorName()"
                    [readOnly]="inputIsReadOnly()"
                />
            } @else {
                <input
                    [class.is-invalid]="ngControl.touched && ngControl.invalid"
                    [type]="type()"
                    [class]="customClasses().input"
                    [formControl]="ngControl.control | formControl"
                    [placeholder]="placeholder() ?? ''"
                    inputmode="numeric"
                    [pattern]="inputDecimalPattern()"
                    digitOnly
                    [decimal]="!inputIntegerOnlyCheckEnabled()"
                    (keyup.enter)="onKeyUpEnter()"
                    [attr.data-qa]="testAutomationSelectorName()"
                    [readOnly]="inputIsReadOnly()"
                />
            }

            @if (inputGroupAppendEnabled()) {
                @if (inputGroupAppendText()) {
                    <div class="input-group-text">{{ inputGroupAppendText() }}</div>
                }
                @if (inputGroupAppendButtonText() || inputGroupAppendButtonIcon()) {
                    <button
                        [class]="inputGroupAppendButtonCustomClasses()?.button"
                        type="button"
                        (click)="onAppendClick()"
                        [disabled]="inputGroupAppendButtonProcessing() || inputGroupAppendButtonDisabled()"
                    >
                        @if (inputGroupAppendButtonProcessing()) {
                            <span
                                [class]="inputGroupAppendButtonCustomClasses()?.spinner"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        }
                        @if (inputGroupAppendButtonIcon()) {
                            <span [class]="inputGroupAppendButtonIcon()"></span>
                        }
                        {{ inputGroupAppendButtonText() }}
                    </button>
                }
            }
        </div>
    }

    <ng-template #plainInput>
        @if (!inputDecimalCheckEnabled()) {
            <input
                [class.is-invalid]="ngControl.touched && ngControl.invalid"
                [type]="type()"
                [class]="customClasses().input"
                [formControl]="ngControl.control | formControl"
                [placeholder]="placeholder() ?? ''"
                [step]="inputStepCount()"
                [min]="inputStepMin()"
                (blur)="onInputFocus(false)"
                (focus)="onInputFocus(true)"
                (keyup.enter)="onKeyUpEnter()"
                [attr.data-qa]="testAutomationSelectorName()"
                [readOnly]="inputIsReadOnly()"
            />
        } @else {
            <input
                [class.is-invalid]="ngControl.touched && ngControl.invalid"
                [type]="type()"
                [class]="customClasses().input"
                [formControl]="ngControl.control | formControl"
                [placeholder]="placeholder() ?? ''"
                inputmode="numeric"
                [pattern]="inputDecimalPattern()"
                digitOnly
                [decimal]="!inputIntegerOnlyCheckEnabled()"
                (keyup.enter)="onKeyUpEnter()"
                [attr.data-qa]="testAutomationSelectorName()"
                [readOnly]="inputIsReadOnly()"
            />
        }
    </ng-template>

    <!-- Error slot: always rendered so sibling layout never shifts -->
    <div class="field-error-slot">
        @if (ngControl.touched) {
            @if (ngControl.control?.errors?.['required']) {
                <div class="invalid-feedback" [attr.data-qa]="testAutomationSelectorName() + '_validation_required'">
                    {{ label() | titlecase }} cannot be empty
                </div>
            } @else if (ngControl.control?.errors?.['email']) {
                <div class="invalid-feedback" [attr.data-qa]="testAutomationSelectorName() + '_validation_email'">
                    {{ label() }} is not a valid email address
                </div>
            } @else if (ngControl.control?.errors?.['min'] || ngControl.control?.errors?.['minlength']) {
                <div class="invalid-feedback" [attr.data-qa]="testAutomationSelectorName() + '_validation_min'">
                    {{ label() }} must be at least
                    {{
                        ngControl.control?.errors?.['min']
                            ? ngControl.control?.errors?.['min']['min']
                            : ngControl.control?.errors?.['minlength']['requiredLength']
                    }}
                    {{ ngControl.control?.errors?.['min'] ? '' : 'characters' }}
                </div>
            } @else if (ngControl.control?.errors?.['max'] || ngControl.control?.errors?.['maxlength']) {
                <div class="invalid-feedback" [attr.data-qa]="testAutomationSelectorName() + '_validation_max'">
                    {{ label() }} must be at most
                    {{
                        ngControl.control?.errors?.['max']
                            ? ngControl.control?.errors?.['max']['max']
                            : ngControl.control?.errors?.['maxlength']['requiredLength']
                    }}
                    {{ ngControl.control?.errors?.['max'] ? '' : 'characters' }}
                </div>
            } @else if (ngControl.control?.errors?.['NoPasswordMatch']) {
                <div class="invalid-feedback" [attr.data-qa]="testAutomationSelectorName() + '_validation_password_match'">
                    Passwords do not match
                </div>
            } @else if (ngControl.control?.errors?.['pattern']) {
                <div class="invalid-feedback" [attr.data-qa]="testAutomationSelectorName() + '_validation_pattern'">
                    {{ label() }} does not follow the required format
                </div>
            } @else {
                @for (entry of customValidationMessages() | keyvalue; track entry.key) {
                    @if (ngControl.control?.errors?.[entry.key]) {
                        <div
                            class="invalid-feedback"
                            [attr.data-qa]="testAutomationSelectorName() + '_validation_custom_' + entry.key"
                        >
                            {{ entry.value }}
                        </div>
                    }
                }
            }
        }
    </div>
</div>
